import { useCallback, RefObject, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { isEmptyField } from 'builder/modules/resumeEditor/resumeScoreRules/sectionRule'
import { isEmptySection } from 'builder/modules/resumeEditor/aiProfileRules'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  SectionNames,
  selectors,
  actions,
  AI_PROFILE_SUMMARY_ANALYTICS_LABEL,
} from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { selectors as userSelectors } from 'builder/modules/user'
import { AiSummaryGenerateType } from 'builder/modules/constants'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { useRemainingAiProfileAttempts } from 'builder/hooks/useRemainingAiProfileAttempts'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { useTourConfig } from 'builder/components/DocumentEditor/DocumentTour'
import { AiKeywordsTogglerForPopups, useKeywords } from 'builder/components/AiFeatures/Keywords'
import { AddPreWrittenPhrasesButton, Divider } from '../styles'
import { Props } from '../types'
import { DIALOG_ANALYTICS_LABEL } from '../const'

const useAiProfileSummary = (props: Props, container: RefObject<HTMLElement>) => {
  const { phrasesOpenerRef, editorContainerRef, onToggleSuggestions } = props
  const resume = useTypedSelector(selectors.resume)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { onChangePhrasesSuggestions } = useTourConfig()
  const isPremiumUser = useTypedSelector(userSelectors.premiumAccess)
  const remainingAIGenerationAttempts = useRemainingAiProfileAttempts()
  const isAiProfileSummaryModalOpen = useTypedSelector(selectors.isAiProfileSummaryModalOpen)
  const isWorkExperienceEmpty = isEmptySection(resume, SectionNames.workExperiences)
  const isProfileEmpty = isEmptyField(resume, SectionNames.profile)
  const isLinkedToJobPosting = !!useTypedSelector(selectors.tailoredJobPostingId)
  const { isKeywordsAvailable } = useKeywords()

  const infoText = useMemo(() => {
    if (isWorkExperienceEmpty) {
      return i18n.t('builder.ai_profile_summary.desc.no_work_experience')
    } else {
      if (!isWorkExperienceEmpty && isProfileEmpty) {
        return i18n.t('builder.ai_profile_summary.desc.no_profile_summary')
      } else if (!isProfileEmpty) {
        return i18n.t('builder.ai_profile_summary.desc.profile_summary_present')
      }
    }
  }, [i18n, isProfileEmpty, isWorkExperienceEmpty])

  const isImproveActive = useMemo(() => {
    if (isWorkExperienceEmpty) {
      return false
    } else {
      if (!isWorkExperienceEmpty && isProfileEmpty) {
        return false
      } else if (!isProfileEmpty) {
        return true
      }
    }
  }, [isProfileEmpty, isWorkExperienceEmpty])

  const badgeText = useMemo(() => {
    if (remainingAIGenerationAttempts === Infinity) {
      return i18n.t('builder.ai_profile_summary.assistance_modal.unlimited')
    } else {
      return `${remainingAIGenerationAttempts} ${i18n.t(
        'builder.ai_profile_summary.assistance_modal.left',
      )}`
    }
  }, [i18n, remainingAIGenerationAttempts])

  const badgeTooltipText = useMemo(() => {
    if (isPremiumUser) {
      return i18n.t('builder.ai_profile_summary.tooltip.premium_user')
    } else {
      if (remainingAIGenerationAttempts === 0) {
        return i18n.t('builder.ai_profile_summary.tooltip.no_attempts_left')
      } else {
        return i18n.t('builder.ai_profile_summary.tooltip.free_user', {
          attempts_left: remainingAIGenerationAttempts,
        })
      }
    }
  }, [i18n, isPremiumUser, remainingAIGenerationAttempts])

  useClickOutsideExcept(container, [phrasesOpenerRef, editorContainerRef], () => {
    if (isAiProfileSummaryModalOpen) {
      dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))
      onChangePhrasesSuggestions()
    }
  })

  useEscapeHandler(() => {
    dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))
    onChangePhrasesSuggestions()
  })

  const handleGenerate = useCallback(() => {
    trackInternalEvent('click_generate', {
      section: SectionNames.profile,
      label: DIALOG_ANALYTICS_LABEL,
      profile_empty: isProfileEmpty,
      experience_empty: isWorkExperienceEmpty,
    })

    dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))

    if (!remainingAIGenerationAttempts) {
      dispatch(actions.setAiGenerationPaywallType('profile'))
      return
    }

    if (isWorkExperienceEmpty) {
      dispatch(
        actions.highlightEmptyWorkExperience({
          title: i18n.t('builder.ai_profile_summary.work_experience.title'),
          content: i18n.t('builder.ai_profile_summary.work_experience.description'),
          buttonLabel: i18n.t('builder.ai_profile_summary.work_experience.button_text'),
        }),
      )
      return
    }

    dispatch(actions.setAIProfileActionType(AiSummaryGenerateType.generate))

    if (isLinkedToJobPosting) {
      dispatch(actions.setIsProfileSummaryWithTailoredResumeModalOpen(true))
    } else {
      dispatch(actions.generateResumeAiProfileSummary())

      trackInternalEvent('start_generating', {
        section: SectionNames.profile,
        label: AI_PROFILE_SUMMARY_ANALYTICS_LABEL,
        profile_empty: isProfileEmpty,
        mode: AiSummaryGenerateType.generate,
      })
    }
  }, [
    isLinkedToJobPosting,
    dispatch,
    isProfileEmpty,
    isWorkExperienceEmpty,
    remainingAIGenerationAttempts,
    i18n,
  ])

  const handleImprove = useCallback(() => {
    dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))

    if (remainingAIGenerationAttempts > 0) {
      dispatch(actions.setAIProfileActionType(AiSummaryGenerateType.improve))
      if (isLinkedToJobPosting) {
        dispatch(actions.setIsIncludeJobPostingToProfileSummary(true))
        dispatch(actions.setIsProfileSummaryWithTailoredResumeModalOpen(true))
      } else {
        dispatch(actions.generateResumeAiProfileSummary())

        trackInternalEvent('start_generating', {
          section: SectionNames.profile,
          label: AI_PROFILE_SUMMARY_ANALYTICS_LABEL,
          profile_empty: isProfileEmpty,
          mode: AiSummaryGenerateType.improve,
        })
      }
    } else {
      dispatch(actions.setAiGenerationPaywallType('profile'))
    }

    trackInternalEvent('click_improve', {
      section: SectionNames.profile,
      label: DIALOG_ANALYTICS_LABEL,
      profile_empty: isProfileEmpty,
      experience_empty: isWorkExperienceEmpty,
    })
  }, [
    remainingAIGenerationAttempts,
    isProfileEmpty,
    isWorkExperienceEmpty,
    isLinkedToJobPosting,
    dispatch,
  ])

  const renderPrewrittenAction = () => {
    return (
      <AddPreWrittenPhrasesButton
        onClick={() => {
          onToggleSuggestions({ trigger: 'click' })
          trackInternalEvent('click_add_phrases', {
            section: SectionNames.profile,
            label: DIALOG_ANALYTICS_LABEL,
            profile_empty: isProfileEmpty,
            experience_empty: isWorkExperienceEmpty,
          })
        }}
      >
        {i18n.t('builder.ai_profile_summary.option.add_prewritten_phrases')}
      </AddPreWrittenPhrasesButton>
    )
  }

  const renderKeywordsAction = () => {
    if (!isKeywordsAvailable) return null

    return (
      <>
        <Divider />

        <AiKeywordsTogglerForPopups
          section={SectionNames.profile}
          text={i18n.t('builder.ai_profile_summary.assistance_modal.add_keywords')}
          onClick={() => dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))}
        />
      </>
    )
  }

  return {
    badgeText,
    badgeTooltipText,
    infoText,
    isImproveActive,
    useClickOutside,
    handleGenerate,
    handleImprove,
    renderPrewrittenAction,
    isProfileEmpty,
    isWorkExperienceEmpty,
    remainingAIGenerationAttempts,
    renderKeywordsAction,
  }
}

export default useAiProfileSummary
